/**
 * TrendIndicator - Reusable component for showing trend direction
 * Can be used anywhere to show up/down trends with optional icon and styling
 *
 * TASK-872: the direction was judged on the RAW value while the digits were printed to two
 * decimals, so `0.004` rendered `+0.00%` under a green up-arrow — a sign, a colour and an
 * arrow on a printed zero. Both now come from `metricFormat`, which decides on the displayed
 * value, and the absent case says `—` like every other surface instead of a fifth spelling.
 */

import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

import {
  METRIC_ABSENT,
  toneForDirection,
  displayedValue,
  isAbsent,
} from '../../../utils/metricFormat';

export interface TrendIndicatorProps {
  value: number | null;
  showIcon?: boolean;
  showValue?: boolean;
  prefix?: string;
  suffix?: string;
  className?: string;
  iconSize?: 'sm' | 'md' | 'lg';
  colorScheme?: 'default' | 'inverse';
}

const TrendIndicator: React.FC<TrendIndicatorProps> = ({
  value,
  showIcon = true,
  showValue = true,
  prefix = '',
  suffix = '%',
  className = '',
  iconSize = 'sm',
  colorScheme = 'default'
}) => {
  // Icon sizes
  const iconSizes = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };

  // Never show fake data for an absent value.
  if (isAbsent(value)) {
    return (
      <span className={`inline-flex items-center gap-1 text-gray-400 ${className}`}>
        {showIcon && <Minus className={iconSizes[iconSize]} />}
        {showValue && <span>{METRIC_ABSENT}</span>}
      </span>
    );
  }

  // Judged on what the reader SEES: `0.004` prints as `0.00`, so it is neutral, not positive.
  const safeValue = displayedValue(value as number, 2);
  const tone = toneForDirection(value, 2);
  const isPositive = tone === 'positive';
  const isNeutral = tone === 'neutral';

  // Color schemes
  const colors = {
    default: {
      positive: 'text-green-600',
      negative: 'text-red-600',
      neutral: 'text-gray-500'
    },
    inverse: {
      positive: 'text-red-600',
      negative: 'text-green-600',
      neutral: 'text-gray-500'
    }
  };

  const colorClass = isNeutral
    ? colors[colorScheme].neutral
    : isPositive
      ? colors[colorScheme].positive
      : colors[colorScheme].negative;

  const iconClass = iconSizes[iconSize];
  
  // Get appropriate icon
  const TrendIcon = isNeutral ? Minus : isPositive ? TrendingUp : TrendingDown;
  
  // Format value
  const formattedValue = `${prefix}${isPositive ? '+' : ''}${safeValue.toFixed(2)}${suffix}`;
  
  return (
    <span className={`inline-flex items-center gap-1 ${colorClass} ${className}`}>
      {showIcon && <TrendIcon className={iconClass} />}
      {showValue && <span>{formattedValue}</span>}
    </span>
  );
};

export default TrendIndicator;